import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Popup } from "@/uikit";
import styles from "./ParametersDialog.module.scss";
import { FixButton } from "@/elements-react/fix-button";
import FixDropdownHead from "@/elements-react/fix-dropdown-head";
import { FixDialogSelector } from "@/elements-react/fix-dialog-selector";
import { DialogMobile } from "@/elements-react/dialog-mobile";
import { t } from "i18next";
import { Icon } from "@/elements-react/fix-icon";
import close from "@/assets/close.svg";
import { useServices } from "@/services-context";
import { COUNTRIES_LISTENER_NAME } from "@/services/countries";
import { PARAMETERS_LISTENER_NAME } from "@/services/parameters";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel";
import type { FixDropdownOptionItem } from "@/elements-react/fix-dropdown";
import { useMemo } from "preact/hooks";
import { Country } from "@/generated/graphql/b2c";
import { supportedLanguages } from "@/constants/languages";
import { LayoutStyleTypes, LayoutTypes, useLayoutType } from "@/util-react/layout";
import { Placement } from "@floating-ui/react-dom";
import {Templates, useTemplate} from "@/util-react/template.tsx";

interface Props {
  anchor: HTMLElement | null;
  type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  onClose: () => void;
}

export const ParametersDialog: React.FC<Props> = ({ anchor, type, onClose }) => {
  const [changing, setChanging] = useState<"country" | "language" | null>(null);
  const { parameters, countries, customerChannel } = useServices([COUNTRIES_LISTENER_NAME, PARAMETERS_LISTENER_NAME, CUSTOMER_CHANNEL_LISTENER_NAME]);
  const currentLayout = useLayoutType();
  const currentCountry = parameters.getVisibleCountry();
  const currentLanguage = parameters.getVisibleLanguage();
  const isSmallType = [LayoutStyleTypes.TYPE_SM, LayoutStyleTypes.TYPE_XS, LayoutStyleTypes.TYPE_XXS].includes(type);
  const [onClickCross, setOnClickCross] = useState<(() => void) | null>(null);
  const { template } = useTemplate();

  const countryOptions: FixDropdownOptionItem[] = useMemo(
    () =>
      ((countries.getChannelCountries() ?? []) as Country[])
        .map((country) => ({
          value: country.code,
          label: country.name,
          selected: country.code === currentCountry,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [customerChannel.availableCountries, currentCountry],
  );

  // TODO: change languages filter after adding translations
  const langOptions: FixDropdownOptionItem[] = useMemo(
    () =>
      supportedLanguages.filter((item) => customerChannel.languages.includes(item.code)).map((item) => ({
        value: item.code,
        label: item.name,
        icon: <Icon library={item.iconLibrary} name={item.icon} />,
        selected: item.code === currentLanguage,
      })),
    [currentLanguage],
  );

  const getValues = () => {
    return type === LayoutStyleTypes.TYPE_SM
      ? {
          anchor,
          placement: "top",
          type: LayoutStyleTypes.TYPE_SM as const,
          selection: "screen" as const,
        }
      : {
          anchor,
          placement: "bottom-start",
          type: LayoutStyleTypes.TYPE_LG as const,
          selection: "inline" as const,
        };
  };

  const values = getValues();

  useEffect(() => {
    if (type === LayoutStyleTypes.TYPE_SM) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [type]);

  const toggleChange = (type: "country" | "language") => {
    setChanging((prev) => (prev === type ? null : type));
  };

  const renderBox = () => {
    const currentLangOption = langOptions.find((lang) => lang.selected);
    const currentCountryOption = countryOptions.find((country) => country.selected);
    const handleOnClick = () => {
      onClose();
      if (onClickCross) {
        onClickCross();
      }
    };

    return (
      <div className={styles.box}>
        <FixButton variant="text" className={styles.close} onClick={handleOnClick}>
          <Icon width={24} height={24} src={close} />
        </FixButton>
        <div className={styles.title}>
          {t("5b7b139ae3b79d64.are-you-in", "Are you in {{country}}?", {
            country: currentCountryOption?.label,
          })}
        </div>
        <div className={styles.tip}>{t("da97774016b6df82.select-language-country", "Select your country and language for accurate prices and shipping.")}</div>
        <div className={styles.inputs}>
          <FixDropdownHead label={currentCountryOption?.label} active={changing === "country"} onClick={() => toggleChange("country")} type="bold" />
          <FixDropdownHead label={currentLangOption?.label} icon="custom" active={changing === "language"} onClick={() => toggleChange("language")} type="bold">
            {currentLangOption?.icon}
          </FixDropdownHead>
        </div>
        {changing && values.selection === "inline" && renderSelectorInline()}
        <div className={styles.buttons}>
          <FixButton variant="primary" size="medium" fullWidth onClick={applySelection}>
            {t("3c60524cceca8f02.confirm", "Confirm")}
          </FixButton>
        </div>
      </div>
    );
  };

  const renderSelectorInline = () => {
    const items = createOptions();
    const searchable = changing === "country";
    return <FixDialogSelector withIcon={false} isSearchable={searchable} type="parameters-inline" items={items} onSelect={confirmSelection} />;
  };

  const renderSelectorScreen = () => {
    const items = createOptions();
    const searchable = true;
    const label = changing === "country" ? "Country" : "Language";
    return (
      <DialogMobile label={label} type="fullsize" onClose={() => setChanging(null)}>
        <FixDialogSelector isSearchable={searchable} type="parameters-screen" items={items} onSelect={confirmSelection} />
      </DialogMobile>
    );
  };

  const createOptions = () => {
    if (changing === "country") return countryOptions;
    if (changing === "language") return langOptions;
    return [];
  };

  const confirmSelection = (code: string) => {
    if (changing === "country") {
      parameters.changeParameter("country", code);
    }
    if (changing === "language") {
      parameters.changeParameter("language", code);
    }
    setChanging(null);
  };

  const applySelection = () => {
    parameters.applyParameters();
    onClose();
  };

  return (
    <>
      {currentLayout.layoutType === LayoutTypes.TYPE_MOBILE && changing
        ? renderSelectorScreen()
        : createPortal(
            <Popup
              active={true}
              anchor={anchor as HTMLElement}
              placement={values.placement as Placement}
              className={classNames(styles.parametersPopup, styles[`type-${type}`])}
              isTransparent={!isSmallType}
              onClose={onClose}
              setCloseCallback={setOnClickCross}
            >
              {renderBox()}
            </Popup>,
            document.body,
          )}
    </>
  );
};
